import MDBox from "components/MDBox";
import { t } from "i18next";
import React, { useEffect, useState } from "react";

import useAPI from "hooks/useAPI";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Skeleton,
  Grid,
  Card,
} from "@mui/material";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { styled } from "@mui/material/styles";
import moment from "moment";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Unauthorized from "components/UI/Unauthorized";
import { usePermissions } from "context/PermissionsContext";

const ColorBar = styled("div")(({ status }) => ({
  width: "0.5rem",
  height: "2.5rem",
  position: "absolute",
  left: "10px",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: status === "running" ? "rgba(49,196,141,1)" : "rgba(249,128,128,1)",
  borderRadius: "25px",
}));

const theme = createTheme({});

const tableStyles = {
  borderRadius: "8px",
  boxShadow: 3,
  overflow: "hidden",
};

const headerStyles = {
  backgroundColor: "#f5f5f5",
  height: "50%",
  "& .MuiTableCell-root": {
    padding: "8px 16px",
    fontSize: "0.875rem",
  },
};

const KafkaData = ({ isIframe = true }) => {
  const { hasPermission } = usePermissions();
  const requiredPermission = "can_view_smm";
  if (!hasPermission(requiredPermission)) {
    return <Unauthorized />;
  }
  const backUrlForIframe = "/worker-status";

  const { swm_api } = useAPI();
  const [kafkaData, setKafkaData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchKafkaData = async () => {
      try {
        const { data } = await swm_api.get("get-kafka-details/");
        setKafkaData(data);
      } catch (error) {
        console.error("Error fetching Kafka data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchKafkaData();
  }, []);

  return (
    <>
      <MDBox pb={3} pt={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {t("Kafka Containers")}
                </MDTypography>
                <Link to={isIframe ? backUrlForIframe : "/"}>
                  <MDButton variant="outlined" color="white" size="small">
                    {t("lang.actions.back")}
                  </MDButton>
                </Link>
              </MDBox>
              <MDBox p={3}>
                <ThemeProvider theme={theme}>
                  <TableContainer component={Paper} sx={tableStyles}>
                    <Table>
                      <TableHead>
                        <TableRow sx={headerStyles}>
                          <TableCell width="3%"></TableCell>
                          <TableCell>{t("lang.container_id")}</TableCell>
                          <TableCell>{t("lang.name")}</TableCell>
                          <TableCell>{t("lang.created")}</TableCell>
                          <TableCell>{t("lang.status")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {kafkaData
                          ? kafkaData.containers.map((container) => (
                              <TableRow key={container.id} sx={{ position: "relative" }}>
                                <TableCell
                                  sx={{
                                    position: "relative",
                                    padding: "0 16px",
                                    width: "24px",
                                  }}
                                >
                                  <ColorBar status={container.status} />
                                </TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>{container.id}</TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>
                                  {container.name.toUpperCase()}
                                </TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>
                                  {moment(container.created).format("D/M/Y H:mm:ss")}
                                </TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>
                                  {container.status === "running" ? (
                                    <Chip
                                      label={t("lang.running")}
                                      sx={{
                                        backgroundColor: "rgba(49,196,141,1)",
                                        color: "white",
                                        fontWeight: "500",
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label={t("lang.stopped")}
                                      sx={{
                                        backgroundColor: "rgba(249,128,128,1)",
                                        color: "white",
                                        fontWeight: "500",
                                      }}
                                    />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          : Array.from({ length: 5 }).map((_, index) => (
                              <TableRow key={index}>
                                {Array.from({ length: 5 }).map((_, index) => (
                                  <TableCell key={index}>
                                    <Skeleton />
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </ThemeProvider>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </>
  );
};

KafkaData.propTypes = {
  isIframe: PropTypes.bool,
};

KafkaData.defaultProps = {
  isIframe: true,
};

export default KafkaData;
